import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, TrendingUp } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import PageHeader from '@/components/PageHeader';
import { getMotorTests } from '@/utils/api';

export default function StoricoClinico({ patient }) {
  const [tappingTests, setTappingTests] = useState([]);
  const [tremorTests, setTremorTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTests = async () => {
      try {
        const [tappingRes, tremorRes] = await Promise.all([
          getMotorTests(patient.id, 'tapping'),
          getMotorTests(patient.id, 'tremor'),
        ]);

        setTappingTests(tappingRes.data.slice(0, 10).reverse());
        setTremorTests(tremorRes.data.slice(0, 10).reverse());
      } catch (error) {
        console.error('Errore nel caricamento test:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTests();
  }, [patient.id]);

  const tappingChartData = tappingTests.map((test, idx) => ({
    nome: `T${idx + 1}`,
    punteggio: test.punteggio,
    data: new Date(test.timestamp).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }),
  }));

  const tremorChartData = tremorTests.map((test, idx) => ({
    nome: `T${idx + 1}`,
    punteggio: test.punteggio,
    data: new Date(test.timestamp).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }),
  }));

  if (loading) {
    return (
      <div className="page-container pb-24">
        <PageHeader title="Storico Clinico" subtitle="Analisi delle performance" icon={FileText} />
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container pb-24">
      <PageHeader title="Storico Clinico" subtitle="Analisi delle performance" icon={FileText} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-slate-200/60 mb-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Finger Tapping Test</h2>
            <p className="text-sm text-slate-600">Ultimi 10 test</p>
          </div>
        </div>

        {tappingChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tappingChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="nome" stroke="#64748b" style={{ fontSize: '14px' }} />
              <YAxis stroke="#64748b" style={{ fontSize: '14px' }} domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '12px',
                  fontSize: '14px',
                }}
                labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
              />
              <Legend wrapperStyle={{ fontSize: '14px' }} />
              <Bar dataKey="punteggio" fill="#2563eb" radius={[8, 8, 0, 0]} name="Punteggio" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-slate-500 py-8">Nessun test disponibile</p>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-slate-200/60"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Test del Tremore</h2>
            <p className="text-sm text-slate-600">Ultimi 10 test</p>
          </div>
        </div>

        {tremorChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={tremorChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="nome" stroke="#64748b" style={{ fontSize: '14px' }} />
              <YAxis stroke="#64748b" style={{ fontSize: '14px' }} domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '12px',
                  fontSize: '14px',
                }}
                labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
              />
              <Legend wrapperStyle={{ fontSize: '14px' }} />
              <Line
                type="monotone"
                dataKey="punteggio"
                stroke="#16a34a"
                strokeWidth={3}
                dot={{ fill: '#16a34a', r: 6 }}
                activeDot={{ r: 8 }}
                name="Punteggio Stabilità"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-slate-500 py-8">Nessun test disponibile</p>
        )}
      </motion.div>
    </div>
  );
}
